// File: frontend/src/components/ui/LoadingState.js
//
// §13.2 — shape-matching skeletons. Not large centred spinners, and never over
// data the operator is already reading.
//
// THE TWO RULES THIS FILE ENFORCES
//
//   1. THE SKELETON HAS THE SHAPE OF WHAT IS COMING. A single full-width grey
//      bar per table row is a spinner wearing a table's clothes: it tells the
//      reader nothing about how many columns there are, which of them are
//      numeric, or how wide the thing they are waiting for will be. The
//      skeleton row here takes the SAME column descriptors the real header
//      takes, so a right-aligned money column skeletons right-aligned and a
//      column that is hidden at this breakpoint skeletons hidden too. When the
//      data lands, nothing jumps.
//
//   2. A REFRESH DOES NOT BLANK THE SCREEN. `shouldSkeleton()` is the decision,
//      written once: show a skeleton only when there is nothing trustworthy to
//      show. A table that already holds rows and is merely re-fetching keeps
//      its rows and gets `RefreshBar` — a 2px indeterminate line and a polite
//      live-region announcement — instead of dissolving into grey. An operator
//      mid-sentence through a reconciliation does not lose their place because
//      a poll came back.
//
// Both are theme-aware, and the pulse stops for `prefers-reduced-motion` (§13.6)
// via ui/motion.js — before this phase every skeleton in the app pulsed forever
// regardless of the setting.

import React from "react";
import {
  Box, Skeleton, CircularProgress, Stack, LinearProgress,
  TableRow as MuiTableRow, TableCell as MuiTableCell,
} from "@mui/material";

import { SR_ONLY } from "./tokens";
import { REDUCED_MOTION_QUERY, skeletonSx } from "./motion";

// MUI's own Skeleton animation is a CSS animation on a pseudo-element, so it
// also has to be told to stop; `animation={false}` under the query is not
// reachable declaratively, hence the sx override.
const MUI_SKELETON_STILL = {
  [`@media ${REDUCED_MOTION_QUERY}`]: {
    "&::after": { animation: "none" },
    animation: "none",
  },
};

/**
 * THE decision about whether to show a skeleton at all (§13.2).
 *
 * @param {boolean} loading      a request is in flight
 * @param {boolean} hasData      there is already something worth reading
 * @param {boolean} [stale]      the caller KNOWS the held data no longer answers
 *                               the question being asked (the filter changed,
 *                               the tenant changed). Only then is blanking right.
 * @returns {boolean}
 */
export function shouldSkeleton(loading, hasData, stale = false) {
  if (!loading) return false;
  if (stale) return true;
  return !hasData;
}

// A block of skeleton lines — good stand-in for a loading card body.
export function SkeletonLines({ lines = 3, gap = 1.25, height = 16 }) {
  return (
    <Stack spacing={gap} sx={{ width: "100%" }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={height}
          width={i === lines - 1 ? "60%" : "100%"}
          animation="wave"
          sx={MUI_SKELETON_STILL}
        />
      ))}
    </Stack>
  );
}

/**
 * One skeleton bar, shaped and aligned like the thing it stands in for.
 * Used directly by surfaces that build their own row layouts.
 */
export function SkeletonBlock({ width = "100%", height = 18, radius = 1, align = "left", sx }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start",
        ...sx,
      }}
    >
      <Box aria-hidden sx={skeletonSx({ width, height, radius })} />
    </Box>
  );
}

/**
 * Skeleton rows for a table body.
 *
 * BACKWARD COMPATIBLE: `columns` still accepts the array of numbers it always
 * accepted (`[24, 140, 80]`), and behaves exactly as before. It now ALSO accepts
 * column descriptors, which is what makes the skeleton match the table's shape:
 *
 *   columns={[
 *     { skeletonWidth: 140, align: "left" },
 *     { skeletonWidth: 80,  align: "right", display: { xs: "none", md: "table-cell" } },
 *   ]}
 *
 * `display` is passed straight through to the cell so a column that is hidden at
 * this breakpoint does not skeleton a phantom ninth column onto a phone.
 */
export function SkeletonTableRows({
  rows = 5,
  columns,
  cols: colCount,
  // Default to MUI's own row and cell. Both used to be REQUIRED, silently: the
  // Ambassador Clients and Earnings pages called this with neither (and with a
  // `cols` count this component did not read), so their very first render —
  // the loading state — rendered an undefined element type. That is React
  // error #130, and the global ErrorBoundary replaced the whole application
  // with a white "Something went wrong" page on every visit.
  TableRow = MuiTableRow,
  TableCell = MuiTableCell,
  cellSx,
}) {
  const list = columns
    || (Number(colCount) > 0 ? Array.from({ length: Number(colCount) }, () => 90) : [24, 140, 80, 60, 60, 90]);
  const cols = list.map((c) => (typeof c === "number" ? { skeletonWidth: c } : (c || {})));

  return Array.from({ length: rows }).map((_, r) => (
    // aria-hidden: a screen reader must not read six rows of nothing. The live
    // announcement is RefreshBar's job, made once, not once per row.
    <TableRow key={r} aria-hidden>
      {cols.map((c, i) => (
        <TableCell
          key={c.key || i}
          align={c.align || "left"}
          sx={{ display: c.display, width: c.width, minWidth: c.minWidth, ...cellSx }}
        >
          <Skeleton
            variant="rounded"
            width={c.skeletonWidth || 90}
            height={c.skeletonHeight || 18}
            animation="wave"
            sx={{
              ...MUI_SKELETON_STILL,
              // Right-aligned columns skeleton right-aligned, so the numeric
              // edge of the table is where it will be when the data lands.
              ml: c.align === "right" ? "auto" : undefined,
              mx: c.align === "center" ? "auto" : undefined,
            }}
          />
        </TableCell>
      ))}
    </TableRow>
  ));
}

/**
 * The refresh indicator for rule 2: shown INSTEAD of a skeleton when there is
 * already trustworthy data on screen.
 *
 * A 2px indeterminate line at the top of the surface and one polite
 * announcement. It does not dim the content, it does not disable it, and it does
 * not move anything — the operator can keep reading and keep scrolling while it
 * runs, which is the entire point.
 */
export function RefreshBar({ active, label = "Refreshing", sx }) {
  return (
    <Box sx={{ position: "relative", height: 2, ...sx }}>
      {active && (
        <>
          <LinearProgress
            aria-hidden
            sx={{
              height: 2,
              // Under reduced motion the indeterminate bar still communicates
              // "busy" through its presence; it just stops sliding.
              [`@media ${REDUCED_MOTION_QUERY}`]: {
                "& .MuiLinearProgress-bar": { animation: "none", transform: "none" },
              },
            }}
          />
          <Box component="span" role="status" aria-live="polite" sx={SR_ONLY}>
            {label}. The figures on screen are the previous ones until it finishes.
          </Box>
        </>
      )}
    </Box>
  );
}

// Centered spinner for small local loads (NOT full-page blocking spinners, and
// NOT a stand-in for a skeleton where the shape of the result is known — §13.2
// names the large centred spinner as the thing to stop doing).
export function Spinner({ size = 28, minHeight = 160, label }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, minHeight, width: "100%" }}>
      <CircularProgress size={size} sx={{ color: "primary.main" }} />
      {label && <Box sx={{ fontSize: 13, color: "text.secondary" }}>{label}</Box>}
    </Box>
  );
}

export default SkeletonLines;
